import { useEffect, useRef, useState } from "react";
import TestimonialBrowse from "./TestimonialBrowse";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { toggleSocialLinksLabels } from "../../store";

function Testimonials({ className }) {
  const dispatch = useDispatch();
  const ref = useRef();
  const { testimonialsData } = useSelector((store) => store.app);
  const testimonialCount = testimonialsData.length;
  const [activeIndex, setActiveIndex] = useState(
    Math.floor(testimonialCount / 2)
  );
  const firstCallImminent = useRef(true);

  /**
   * This is a callback to begin the observation of the div when the component first renders. As the
   * `observer` object is memoized, it won't change across renders.
   */
  useEffect(() => {
    /**
     * An interaction observer object that allows us to monitor any interaction between the testimonial
     * div, and the viewport (visible area).
     */
    const observer = new IntersectionObserver(
      () => {
        /**
         * Here, the social links' labels' display is cotrolled. In case the callback is called
         * for the first time (on beginning observation), it shouldn't be executed, and so a time
         * offset is used.
         */
        if (!firstCallImminent.current) dispatch(toggleSocialLinksLabels());
        else firstCallImminent.current = false;
      },
      {
        root: null,
        threshold: [0.05],
      }
    );
    observer.observe(ref.current);

    /**
     * In case `dispatch` ever changes, the observer will be disconnected in the cleanup, and the
     * `firstCallImminent` flag will be set for the next observer object.
     */
    return () => {
      observer.disconnect();
      firstCallImminent.current = true;
    };
  }, [dispatch]);

  if (testimonialCount > 5) {
    throw Error("Cannot have more than 5 testimonials.");
  }

  const testimonialDivs = testimonialsData.map((testimonial, index) => (
    <div
      className="flex flex-col h-full w-full absolute"
      key={testimonial.id}
      style={{
        left: `${index * 108}%`,
      }}
    >
      <div className="flex flex-row items-center mb-7 font-medium">
        <img
          src={testimonial.pic}
          alt={testimonial.name}
          className="rounded-full w-[70px] h-auto"
        />
        <div className="flex flex-col pl-3">
          <p className="text-2xl">{testimonial.name}</p>
          <p>{testimonial.desgn}</p>
        </div>
      </div>
      <div
        className="overflow-y-auto pr-1"
        onWheelCapture={(e) => e.stopPropagation()}
      >
        <p className="testimonial-text mb-2.5">{testimonial.text}</p>
        {testimonial.link && (
          <a
            href={testimonial.link}
            target="_blank"
            rel="noreferrer"
            className="testimonial-link"
          >
            Full Text
          </a>
        )}
      </div>
    </div>
  ));

  return (
    <div
      className={classNames(
        "h-[53vh] w-[35vw] testimonial-container px-4 text-white flex flex-col overflow-hidden",
        className
      )}
      ref={ref}
    >
      <h1 className="mt-1 mb-4 mx-auto text-center font-bold h-[7%]">
        Testimonials
      </h1>
      <div
        className="w-full h-[72%] flex flex-row relative"
        style={{
          transform: `translate(calc(${activeIndex} * -108%))`,
          transition: "transform 0.3s linear",
        }}
      >
        {testimonialDivs}
      </div>
      <TestimonialBrowse
        onOffset={(offset) =>
          setActiveIndex(
            (activeIndex + offset + testimonialCount) % testimonialCount
          )
        }
        numberOfCircles={testimonialCount}
        activeCircle={activeIndex}
      />
    </div>
  );
}

export default Testimonials;
